import {
	Accordion,
	Button,
	Grid,
	Icon,
	type JVSIcons,
	Popup,
	Search,
} from '@mairistem/composants';
import { deburr } from 'lodash';
import React from 'react';
import './BlockList.css';

interface BlockItem {
	codeBlock: string;
	label: string;
}

interface TypeBlockItemsProps {
	type: string;
	icon: JVSIcons;
	blocks: BlockItem[];
}

interface TypeBlockListProps {
	blocks: TypeBlockItemsProps[];
	onSelect: (block: BlockItem) => void;
	popupTrigger: React.ReactNode;
}

const BlockList: React.FC<TypeBlockListProps> = ({
	blocks,
	onSelect,
	popupTrigger,
}: TypeBlockListProps) => {
	const [activeIndex, setActiveIndex] = React.useState<number[]>([]);
	const [isSearching, setIsSearching] = React.useState(false);
	const [searchResults, setSearchResults] = React.useState<BlockItem[]>([]);

	if (!blocks || blocks.length === 0) {
		return null;
	}

	const handleTitleClick = (
		_e: React.MouseEvent<HTMLDivElement>,
		data: { index: number },
	) => {
		const { index } = data;

		if (activeIndex.includes(index)) {
			setActiveIndex(activeIndex.filter((i) => i !== index));
		} else {
			setActiveIndex([...activeIndex, index]);
		}
	};

	const handleSearchChange = (
		_e: React.ChangeEvent<HTMLInputElement>,
		{ value }: { value: string },
	) => {
		const searchValue = deburr(value.toLowerCase());
		setIsSearching(searchValue.length > 0);

		const results = blocks.flatMap((typeBlock) =>
			typeBlock.blocks.filter((block) =>
				deburr(block.label.toLowerCase()).includes(searchValue),
			),
		);

		setSearchResults(results);
	};

	const getHeader = () => (
		<Search
			className="popup-search"
			placeholder="Rechercher un bloc"
			onSearchChange={handleSearchChange}
			data-testid="blockListSearch"
			input={{ 'data-testid': 'blockListSearchInput' }}
			icon={{
				name: 'search',
				link: true,
				'data-testid': 'blockListSearchIcon',
			}}
			loading={false}
			clearable
			autoComplete="off"
			results={
				searchResults.length > 0
					? searchResults.map((block) => ({
							title: block.label,
						}))
					: []
			}
			noResultsMessage={
				searchResults.length === 0 && isSearching
					? 'Aucun résultat trouvé'
					: null
			}
		/>
	);

	const panels = blocks
		.sort((a, b) => a.type.localeCompare(b.type))
		.map((typeBlock, index) => ({
			key: typeBlock.type,
			title: (
				<Accordion.Title
					data-testid="openAccordionBloks"
					index={index}
					onClick={(e, data) =>
						handleTitleClick(e, { index: Number(data.index) })
					}
				>
					<Icon name="dropdown" />
					{typeBlock.type}
				</Accordion.Title>
			),
			content: (
				<Accordion.Content active={activeIndex.includes(index)}>
					{typeBlock.blocks
						.sort((a, b) => a.label.localeCompare(b.label))
						.map((block) => (
							<Button
								key={block.codeBlock}
								content={
									<Grid>
										<Grid.Column>
											<Grid.Row>
												<Icon name={typeBlock.icon} iconSet="Lucide" />
											</Grid.Row>
											<Grid.Row>{block.label}</Grid.Row>
										</Grid.Column>
									</Grid>
								}
								onClick={() => onSelect(block)}
								data-testid="chooseBlock"
							/>
						))}
				</Accordion.Content>
			),
		}));

	return (
		<Popup
			basic
			trigger={popupTrigger}
			position="bottom right"
			className="block-list-popup"
			wide={false}
			hoverable
			on={['click', 'hover']}
			hideOnScroll
			positionFixed
		>
			<Popup.Header data-testid="blockListHeader">{getHeader()}</Popup.Header>
			<Popup.Content data-testid="blockListContent" className="block-list">
				{isSearching && (
					<div className="block-list-search-results">
						{searchResults.map((block) => (
							<Button
								key={block.codeBlock}
								content={block.label}
								onClick={() => onSelect(block)}
								data-testid="searchResultBlock"
							/>
						))}
					</div>
				)}
				{!isSearching && (
					<Accordion panels={panels} styled fluid exclusive={false} />
				)}
			</Popup.Content>
		</Popup>
	);
};

export default BlockList;
